import { since } from "./format";
import type { Permission } from "./permissions";
import { money } from "./tariff";
import type { AlertSummary, FleetStats, PublicStats } from "./types";
import { resolveLastDays } from "./usageWindow";
import type { ResolvedWindow } from "./usageWindow";

/**
 * The dashboard's decisions, out of the page so a test can reach them: who may
 * open it, which window its figures cover, and what it puts in front of an
 * operator as needing attention.
 */

/** The `/stats/*` endpoints' permissions, one per panel. */
export const DASHBOARD_PERMISSIONS = [
  "charge_points.view",
  "sessions.view",
  "public_orders.view",
] as const satisfies readonly Permission[];

/**
 * The zone a day is counted in. The server draws its daily series in the same
 * one (`DASHBOARD_TIME_ZONE` in `routes/stats.ts`), and the window sent has to
 * start at the midnight the series starts at, or "today" would be half of two days.
 */
export const DASHBOARD_TIME_ZONE = "Asia/Phnom_Penh";

type Can = (permission: Permission) => boolean;

/** Any one panel is a dashboard worth opening; the page draws only the panels held. */
export function canSeeDashboard(can: Can): boolean {
  return DASHBOARD_PERMISSIONS.some((permission) => can(permission));
}

/**
 * Where `/` and a fresh sign-in go. The fleet remains the answer for a principal
 * the dashboard would show nothing to, which is where everybody landed before it.
 */
export function landingPath(can: Can): string {
  return canSeeDashboard(can) ? "/dashboard" : "/fleet";
}

export type DashboardRange = "today" | "last-7" | "last-30";

export const DASHBOARD_RANGES: readonly {
  readonly value: DashboardRange;
  readonly label: string;
}[] = [
  { value: "today", label: "Today" },
  { value: "last-7", label: "7 days" },
  { value: "last-30", label: "30 days" },
];

/** Today unless the URL names one of the three; a stale `?range=month` is not a window here. */
export function readDashboardRange(raw: string | null): DashboardRange {
  return DASHBOARD_RANGES.some((option) => option.value === raw)
    ? (raw as DashboardRange)
    : "today";
}

const RANGE_DAYS: Readonly<Record<DashboardRange, number>> = {
  today: 1,
  "last-7": 7,
  "last-30": 30,
};

/** Whole local days, today included — the fleet pages' rule (`usageWindow.ts`). */
export function resolveDashboardWindow(
  range: DashboardRange,
  zone: string,
  now: Date = new Date(),
): ResolvedWindow {
  const days = RANGE_DAYS[range];
  const label = range === "today" ? "Today" : `Last ${days} days`;
  return { ...resolveLastDays(days, zone, now), label };
}

/**
 * Uptime as a percentage with one decimal, truncated rather than rounded: 99.96%
 * rounded is "100.0", and a clean hundred over a fleet that dropped a station is
 * the one figure on the page an operator would stop checking.
 */
export function uptimeFigure(ratio: number | null): string {
  if (ratio === null) return "—";
  if (ratio >= 1) return "100";
  return (Math.floor(ratio * 1000) / 10).toFixed(1);
}

/**
 * An average session length, split for a `Metric`: figure large, unit small.
 * Seconds below a minute rather than a floor of "1 min" — a window full of failed
 * starts should read as what it is.
 */
export function durationParts(seconds: number | null): {
  readonly value: string;
  readonly unit: string | undefined;
} {
  if (seconds === null) return { value: "—", unit: undefined };
  if (seconds < 60) return { value: String(Math.round(seconds)), unit: "s" };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { value: String(minutes), unit: "min" };
  return {
    value: `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`,
    unit: "h",
  };
}

export interface AttentionItem {
  /** Stable per condition, so a row keeps its identity across polls. */
  readonly key: string;
  /** `faulted` is broken or owed now; `transitional` is waiting on somebody. */
  readonly tone: "faulted" | "transitional";
  readonly title: string;
  readonly detail: string;
  /** Where the operator fixes it. */
  readonly to: string;
}

/**
 * Each source is optional, and absent means "not loaded or not permitted" —
 * never "nothing wrong". Either way it contributes no rows, so an operator who may
 * not see orders gets a list about the fleet rather than a false all-clear about money.
 */
export interface AttentionSources {
  readonly fleet?: FleetStats | undefined;
  readonly publicStats?: PublicStats | undefined;
  readonly alerts?: AlertSummary | undefined;
  readonly now: Date;
}

/** How many station ids a row names before counting the rest. */
const NAMED = 3;

/**
 * Everything on the dashboard that someone has to act on, most urgent first:
 * money and safety, then stations that are down, then the queues that are only
 * waiting, then housekeeping. Warnings are left to the alerts page — they are
 * there to be read in passing, and a list that carries everything is one nobody reads.
 */
export function attentionItems(sources: AttentionSources): readonly AttentionItem[] {
  const { fleet, publicStats, alerts } = sources;
  const queues = publicStats?.queues;
  const now = sources.now.getTime();

  return [
    ...alertRows(alerts, "critical"),
    ...countRow(
      "refund-failed",
      queues?.refundFailed,
      "refund failed",
      "refunds failed",
      "refund_failed",
      "faulted",
    ),
    ...countRow(
      "capture-failed",
      queues?.captureFailed,
      "capture failed",
      "captures failed",
      "capture_failed",
      "faulted",
    ),
    ...faultedRows(fleet, now),
    ...offlineRows(fleet, now),
    ...(queues?.refundOwed ?? []).map(refundOwedRow),
    ...countRow(
      "capture-required",
      queues?.captureRequired,
      "capture waiting",
      "captures waiting",
      "capture_required",
      "transitional",
    ),
    ...alertRows(alerts, "error"),
    ...driftRows(fleet),
  ];
}

function alertRows(
  alerts: AlertSummary | undefined,
  severity: "critical" | "error",
): AttentionItem[] {
  const open = alerts?.open[severity] ?? 0;
  if (open === 0) return [];
  return [
    {
      key: `alerts-${severity}`,
      tone: "faulted",
      title: `${open} ${severity} ${open === 1 ? "alert" : "alerts"} open`,
      detail:
        severity === "critical" ? "Money or safety" : "The server could not resolve these itself",
      to: `/alerts?severity=${severity}`,
    },
  ];
}

function countRow(
  key: string,
  total: number | undefined,
  singular: string,
  plural: string,
  state: string,
  tone: AttentionItem["tone"],
): AttentionItem[] {
  if (total === undefined || total === 0) return [];
  return [
    {
      key,
      tone,
      title: `${total} ${total === 1 ? singular : plural}`,
      detail: "Public charging orders",
      to: `/public-orders?state=${state}`,
    },
  ];
}

function refundOwedRow(row: PublicStats["queues"]["refundOwed"][number]): AttentionItem {
  return {
    key: `refund-owed-${row.currency}`,
    tone: "transitional",
    title: `${row.orders} ${row.orders === 1 ? "refund" : "refunds"} owed: ${money(row.minor, row.currency)}`,
    detail: "Drivers waiting on money back",
    to: "/public-orders?state=refund_required",
  };
}

/**
 * "Has a fault" rather than "is faulted": the server's list is `Faulted` *or* any
 * status reporting an error code, so an Available connector with a ground fault is
 * on it, and saying it is faulted would contradict its own status column.
 */
function faultedRows(fleet: FleetStats | undefined, now: number): AttentionItem[] {
  // `?? 0` for a server one deploy behind, which does not send the total.
  const total = fleet?.faultedTotal ?? 0;
  if (fleet === undefined || total === 0) return [];
  const [first] = fleet.faulted;
  if (total === 1 && first !== undefined) {
    return [
      {
        key: "faulted",
        tone: "faulted",
        title: `${first.chargePointId} connector ${first.connectorId} has a fault`,
        detail: `${first.errorCode} · since ${since(first.since, now)}`,
        to: `/fleet/${encodeURIComponent(first.chargePointId)}`,
      },
    ];
  }
  const stations = [...new Set(fleet.faulted.map((row) => row.chargePointId))];
  return [
    {
      key: "faulted",
      tone: "faulted",
      title: `${total} ${total === 1 ? "connector" : "connectors"} with a fault`,
      detail: named(stations, stations.length),
      to: "/fleet",
    },
  ];
}

function offlineRows(fleet: FleetStats | undefined, now: number): AttentionItem[] {
  if (fleet === undefined || fleet.offlineTotal === 0) return [];
  const total = fleet.offlineTotal;
  const [first] = fleet.offline;
  if (total === 1 && first !== undefined) {
    return [
      {
        key: "offline",
        tone: "faulted",
        title: `${first.chargePointId} is offline`,
        detail:
          first.lastSeenAt === null
            ? "Not seen since records began"
            : `Last seen ${since(first.lastSeenAt, now)}`,
        to: `/fleet/${encodeURIComponent(first.chargePointId)}`,
      },
    ];
  }
  return [
    {
      key: "offline",
      tone: "faulted",
      title: `${total} ${total === 1 ? "station" : "stations"} offline`,
      detail: named(
        fleet.offline.map((row) => row.chargePointId),
        total,
      ),
      to: "/fleet?status=Accepted&connected=false",
    },
  ];
}

function driftRows(fleet: FleetStats | undefined): AttentionItem[] {
  const drift = fleet?.configDrift;
  if (drift === undefined || drift.total === 0) return [];
  const [first] = drift.chargePointIds;
  const isOne = drift.total === 1 && first !== undefined;
  return [
    {
      key: "config-drift",
      tone: "transitional",
      title: isOne
        ? `${first} drifted from its configuration`
        : `${drift.total} stations drifted from their configuration`,
      detail: isOne
        ? "A setting differs from what was last applied"
        : named(drift.chargePointIds, drift.total),
      to: isOne ? `/fleet/${encodeURIComponent(first)}` : "/fleet",
    },
  ];
}

/** "A, B, C and 22 more" — the list is capped server-side, so `total` is the true count. */
function named(ids: readonly string[], total: number): string {
  // A count can arrive a poll ahead of its rows; point at the page rather than
  // print an empty detail.
  if (ids.length === 0) return "See Charge points";
  const shown = ids.slice(0, NAMED);
  const rest = total - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
}
